import React, { Component } from 'react';
import PropTypes from 'prop-types';
import './SlateTextInput.css';

class SlateTextInput extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isFocused: false,
      showPassword: false,
    };
    this.inputRef = React.createRef();
  }

  handleFocus = (e) => {
    this.setState({ isFocused: true });
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  };

  handleBlur = (e) => {
    this.setState({ isFocused: false });
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  };

  togglePasswordVisibility = () => {
    this.setState(function(prevState) {
      return { showPassword: !prevState.showPassword };
    });
  };

  handleChange = (e) => {
    if (this.props.onChange) {
      this.props.onChange(e);
    }
  };

  hasValue = () => {
    const { value } = this.props;
    return value !== null && value !== undefined && value !== '';
  };

  focus = () => {
    if (this.inputRef.current) {
      this.inputRef.current.focus();
    }
  };

  render() {
    const {
      name,
      value,
      placeholder,
      label,
      disabled,
      error,
      type,
      fullWidth,
      autoFocus,
      autoComplete,
      icon,
      hint,
      className,
      inputStyle,
      containerStyle,
    } = this.props;
    const { isFocused, showPassword } = this.state;

    const hasValue = this.hasValue();
    const isLabelFloating = isFocused || hasValue;
    const hasLabel = label !== null && label !== undefined && label !== '';
    const isPasswordField = type === 'password';
    const inputType = isPasswordField && showPassword ? 'text' : type;

    // Build container class names
    const containerClasses = [
      'slate-text-input',
      fullWidth ? 'slate-text-input--full-width' : '',
      disabled ? 'slate-text-input--disabled' : '',
      error ? 'slate-text-input--error' : '',
      isFocused ? 'slate-text-input--focused' : '',
      icon ? 'slate-text-input--has-icon' : '',
      !hasLabel ? 'slate-text-input--no-label' : '',
      isPasswordField ? 'slate-text-input--has-toggle' : '',
      className || '',
    ].filter(Boolean).join(' ');

    // Build label class names
    const labelClasses = [
      'slate-text-input-label',
      isLabelFloating ? 'slate-text-input-label--floating' : '',
    ].filter(Boolean).join(' ');

    const inputValue = value !== null && value !== undefined ? value : '';

    return (
      <div className={containerClasses} style={containerStyle}>
        <div className="slate-text-input-wrapper">
          {icon && (
            <span className="material-symbols-outlined slate-text-input-icon">
              {icon}
            </span>
          )}

          {hasLabel && (
            <label className={labelClasses} htmlFor={name}>
              {label}
            </label>
          )}

          <input
            ref={this.inputRef}
            id={name}
            name={name}
            type={inputType || 'text'}
            value={inputValue}
            placeholder={isLabelFloating || !hasLabel ? placeholder : ''}
            disabled={disabled}
            autoFocus={autoFocus}
            autoComplete={autoComplete}
            className="slate-text-input-field"
            style={inputStyle}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            onChange={this.handleChange}
            onKeyPress={this.props.onKeyPress}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={
              error ? name + '-error' : hint ? name + '-hint' : undefined
            }
          />

          {isPasswordField && (
            <button
              type="button"
              className="slate-text-input-toggle"
              onClick={this.togglePasswordVisibility}
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <svg
                className="slate-text-input-toggle-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {showPassword ? (
                  <>
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </>
                ) : (
                  <>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </>
                )}
              </svg>
            </button>
          )}
        </div>

        {error && (
          <div id={name + '-error'} className="slate-text-input-error" role="alert">
            {error}
          </div>
        )}

        {hint && !error && (
          <div id={name + '-hint'} className="slate-text-input-hint">
            {hint}
          </div>
        )}
      </div>
    );
  }
}

SlateTextInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyPress: PropTypes.func,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  type: PropTypes.string,
  fullWidth: PropTypes.bool,
  autoFocus: PropTypes.bool,
  autoComplete: PropTypes.string,
  icon: PropTypes.string,
  hint: PropTypes.string,
  className: PropTypes.string,
  inputStyle: PropTypes.object,
  containerStyle: PropTypes.object,
};

SlateTextInput.defaultProps = {
  type: 'text',
  disabled: false,
  fullWidth: false,
  autoFocus: false,
};

export default SlateTextInput;
